/**
 * Local Database - Entity Operations (Clients & Suppliers)
 *
 * CRUD operations for clients and suppliers.
 */

import { initializeLocalDb } from './init'
import {
  localStore,
  saveToLocalStorage,
} from './store'

import type { LocalEntity, LocalAccount, EntityType } from './types'

/**
 * Get all entities from local database
 * @param orgId Organization ID
 * @param entityType Optional filter by entity type (client/supplier)
 */
export async function getLocalEntities(orgId: string, entityType?: EntityType): Promise<LocalEntity[]> {
  // Initialize if needed
  if (!localStore.initialized) {
    await initializeLocalDb(orgId)
  }

  return Array.from(localStore.entities.values())
    .filter(e => e.orgId === orgId && (!entityType || e.entityType === entityType))
    .sort((a, b) => a.code.localeCompare(b.code))
}

/**
 * Get entity by ID
 */
export function getLocalEntity(orgId: string, entityId: string): Promise<LocalEntity | null> {
  const entity = localStore.entities.get(entityId)
  return Promise.resolve(entity?.orgId === orgId ? entity : null)
}

/**
 * Create a new entity (client or supplier)
 * Also creates the corresponding sub-account under AR (for clients) or AP (for suppliers)
 */
// eslint-disable-next-line max-lines-per-function -- entity + sub-account creation is a single atomic operation
export function createLocalEntity(
  orgId: string,
  data: Omit<LocalEntity, 'id' | 'orgId' | 'accountId' | 'createdAt' | 'updatedAt'>
): LocalEntity {
  const id = crypto.randomUUID()
  const now = new Date().toISOString()

  // Find the parent account dynamically by type and subtype
  // Clients go under Receivable accounts, Suppliers under Payable accounts
  const parentAccountType = data.entityType === 'client' ? 'ACCOUNT_TYPE_ASSET' : 'ACCOUNT_TYPE_LIABILITY'
  const parentAccountSubtype = data.entityType === 'client' ? 'Receivable' : 'Payable'

  const parentAccount = Array.from(localStore.accounts.values())
    .find(a => a.orgId === orgId &&
               a.accountType === parentAccountType &&
               a.accountSubtype === parentAccountSubtype &&
               !a.memberId) // Parent account should not be member-linked

  /* v8 ignore next 3 */
  if (!parentAccount) {
    throw new Error(`Parent ${parentAccountSubtype} account not found`)
  }

  // Create sub-account for this entity
  const accountId = crypto.randomUUID()
  const accountCode = `${parentAccount.code}-${data.code}`
  const normalBalance = data.entityType === 'client' ? 'NORMAL_BALANCE_DEBIT' : 'NORMAL_BALANCE_CREDIT'

  const subAccount: LocalAccount = {
    id: accountId,
    orgId,
    ledgerId: data.ledgerId,
    code: accountCode,
    name: data.name,
    description: `${data.entityType === 'client' ? 'Accounts Receivable' : 'Accounts Payable'} - ${data.name}`,
    accountType: parentAccountType,
    accountSubtype: parentAccountSubtype,
    normalBalance,
    parentId: parentAccount.id,
    path: `${parentAccount.path}/${accountCode}`,
    depth: parentAccount.depth + 1,
    isHeader: false,
    isSystem: false,
    isActive: true,
    memberId: id, // Link the account to the entity
    createdAt: now,
    updatedAt: now,
  }
  localStore.accounts.set(accountId, subAccount)

  // Create the entity
  const entity: LocalEntity = {
    ...data,
    id,
    orgId,
    accountId,
    createdAt: now,
    updatedAt: now,
  }

  localStore.entities.set(id, entity)
  saveToLocalStorage()
  return entity
}

/**
 * Update an entity
 */
export async function updateLocalEntity(
  orgId: string,
  entityId: string,
  data: Partial<Omit<LocalEntity, 'id' | 'orgId' | 'accountId' | 'entityType' | 'createdAt' | 'updatedAt'>>
): Promise<LocalEntity> {
  const existing = await getLocalEntity(orgId, entityId)
  if (!existing) {throw new Error('Entity not found')}

  const updated: LocalEntity = {
    ...existing,
    ...data,
    id: existing.id,
    orgId: existing.orgId,
    accountId: existing.accountId,
    entityType: existing.entityType,
    createdAt: existing.createdAt,
    updatedAt: new Date().toISOString(),
  }

  // Also update the linked account name if entity name changed
  if (data.name && data.name !== existing.name) {
    const account = localStore.accounts.get(existing.accountId)
    if (account) {
      account.name = data.name
      /* v8 ignore next */
      account.description = `${existing.entityType === 'client' ? 'Accounts Receivable' : 'Accounts Payable'} - ${data.name}`
      account.updatedAt = updated.updatedAt
    }
  }

  localStore.entities.set(entityId, updated)
  saveToLocalStorage()
  return updated
}

/**
 * Deactivate an entity
 */
export function deactivateLocalEntity(orgId: string, entityId: string): Promise<LocalEntity> {
  return updateLocalEntity(orgId, entityId, { isActive: false })
}

/**
 * Reactivate an entity
 */
export function reactivateLocalEntity(orgId: string, entityId: string): Promise<LocalEntity> {
  return updateLocalEntity(orgId, entityId, { isActive: true })
}

/**
 * Get next entity code
 */
export function getNextEntityCode(entityType: EntityType): string {
  const prefix = entityType === 'client' ? 'C' : 'S'
  const existing = Array.from(localStore.entities.values())
    .filter(e => e.entityType === entityType)
    .map(e => {
      const match = e.code.match(/^[CS](\d+)$/)
      const numStr = match?.[1]
      /* v8 ignore next */
      return numStr ? parseInt(numStr, 10) : 0
    })

  const maxNum = existing.length > 0 ? Math.max(...existing) : 0
  return `${prefix}${String(maxNum + 1).padStart(3, '0')}`
}
